import { friendlyError } from "@/lib/friendly-errors";
import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Moon,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  X,
  Star,
  Cloud,
  Leaf,
  Heart,
  Wind,
  CheckCircle2,
  Lightbulb,
  Compass,
} from "lucide-react";
import { BeforeYouBegin } from "@/components/BeforeYouBegin";
import { createJournalEntry, getJournalEntries, type AiResponse } from "@/lib/journal.functions";
import {
  JOURNEY_TOPICS,
  WOUNDS,
  REPARENTING_NEEDS,
  type JourneyTopic,
  type Wound,
  type ReparentingNeed,
} from "@/lib/shadow-work";
import { TopicFilter } from "@/components/TopicFilter";
import { FavoriteButton } from "@/components/FavoriteButton";
import { matchesTopic, topicList } from "@/lib/topics";
import { JournalGuide } from "@/components/JournalGuide";

const TOPICS = topicList(["innerchild", "selfworth", "anger", "grief", "patterns", "integration"]);

export const Route = createFileRoute("/shadow-work")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Shadow Work — A Guided Recovery Journey" },
      {
        name: "description",
        content:
          "A trauma-informed, guided shadow work journey. Meet your inner critic, inner child, wounds, and boundaries with compassion and AI-supported reflection.",
      },
      { property: "og:title", content: "Shadow Work — A Guided Recovery Journey" },
      { property: "og:description", content: "A trauma-informed, guided shadow work journey. Meet your inner critic, inner child, wounds, and boundaries with compassion and AI-supported reflection." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/shadow-work" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/shadow-work" }],
  }),
  component: withMemberGate(ShadowWorkPage, "Shadow Work"),
});

const ACCENT: Record<JourneyTopic["accent"], string> = {
  rose: "from-[#f7d9d2] to-[#f0bcb4]",
  lavender: "from-[#e3d4f7] to-[#d9c8f5]",
  sage: "from-[#d8e6d2] to-[#bfd4b6]",
  blush: "from-[#fbe1e0] to-[#f5cbc9]",
  cream: "from-[#f7ecd5] to-[#efdcb6]",
  beige: "from-[#ede4d3] to-[#dcc9ad]",
};

function ShadowWorkPage() {
  const [activeTopic, setActiveTopic] = useState<JourneyTopic | null>(null);
  const [activeWound, setActiveWound] = useState<Wound | null>(null);
  const [activeNeed, setActiveNeed] = useState<ReparentingNeed | null>(null);
  const [topic, setTopic] = useState<string | null>(null);
  const visibleJourney = JOURNEY_TOPICS.filter((t) => matchesTopic(t, topic));
  const visibleWounds = WOUNDS.filter((w) => matchesTopic(w, topic));
  const visibleNeeds = REPARENTING_NEEDS.filter((n) => matchesTopic(n, topic));
  const [completed, setCompleted] = useState<Set<string>>(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const raw = localStorage.getItem("rt:shadow:completed");
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set();
    }
  });

  const markComplete = (id: string) => {
    setCompleted((prev) => {
      const next = new Set(prev);
      next.add(id);
      try {
        localStorage.setItem(
          "rt:shadow:completed",
          JSON.stringify([...next]),
        );
      } catch {
        /* noop */
      }
      return next;
    });
  };

  return (
    <BeforeYouBegin
      title="💛 Before You Begin"
      storageKey="rt:gate:shadow-work"
      intro={
        <>
          <p>This is a space for gentle self-reflection.</p>
        </>
      }
      items={[
        { emoji: "🌿", text: <>Move at your own pace.</> },
        { emoji: "🌿", text: <>Skip anything that doesn't feel supportive.</> },
        { emoji: "🌿", text: <>Take breaks whenever you need them.</> },
        {
          emoji: "🌿",
          text: (
            <>
              If you feel overwhelmed,{" "}
              <strong className="font-semibold">Ground Me Now</strong> is always
              available.
            </>
          ),
        },
      ]}
      footer={
        <p className="italic">🌿 Only take what feels helpful today.</p>
      }
      continueLabel="💛 I'm ready to begin"
    >

      <div className="relative min-h-dvh overflow-hidden bg-background">
        <AmbientBackground />

        <div className="relative mx-auto max-w-5xl px-4 py-12 sm:py-16">
          {/* Header */}
          <header className="mb-12 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#d9c8f5]/60 bg-white/60 px-4 py-1.5 text-xs text-foreground/80 backdrop-blur dark:border-[#9b7cc7]/40 dark:bg-white/5">
              <Moon className="h-3.5 w-3.5 text-[#9b7cc7] dark:text-[#c9b6ec]" />
              A Guided Recovery Journey
            </div>
            <h1 className="font-serif text-4xl leading-tight text-foreground sm:text-5xl">
              Shadow Work
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-foreground/75">
              Meeting the parts of you that learned to hide — slowly, gently,
              with curiosity. There is no wrong way to begin.
            </p>

            {completed.size > 0 && (
              <div className="mx-auto mt-6 inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-1.5 text-xs text-foreground/80 backdrop-blur dark:bg-white/5">
                <Sparkles className="h-3.5 w-3.5 text-[#c48bb8] dark:text-[#e2a6d0]" />
                {completed.size}{" "}
                {completed.size === 1 ? "reflection" : "reflections"} on your
                journey
              </div>
            )}
          </header>
          <JournalGuide journal="shadow" className="mb-10" />

          {/* Gentle note */}
          <div className="mb-12 rounded-3xl border border-[#d9c8f5]/50 bg-white/60 p-6 backdrop-blur dark:border-[#9b7cc7]/30 dark:bg-card/60">
            <p className="text-sm leading-relaxed text-foreground/85">
              <strong className="font-medium text-[#7a5cb0] dark:text-[#c9b6ec]">
                A gentle note.
              </strong>{" "}
              Shadow work can stir tender feelings. Move at your own pace, and
              return to your{" "}
              <Link
                to="/"
                className="font-medium text-[#7a5cb0] underline underline-offset-2 dark:text-[#c9b6ec]"
              >
                Nervous System Toolkit
              </Link>{" "}
              anytime you need to soften.
            </p>
          </div>

          {/* Entry point to the illustrated 12-part journey */}
          <Link
            to="/shadow-journey"
            className="mb-12 block overflow-hidden rounded-3xl border border-[#C9A227]/40 bg-gradient-to-br from-[#171412] to-[#0f0d0b] p-6 transition hover:border-[#C9A227]"
          >
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C9A227]">
              New — an illustrated journal
            </span>
            <span className="mt-2 block font-serif text-2xl text-[#F3DFA2]">
              The Guided Shadow Journal
            </span>
            <span className="mt-2 block max-w-xl text-sm leading-relaxed text-[#e8e2d8]/75">
              Twelve private sections — masks, hidden emotions, shame, the inner
              critic, relationship patterns and reclaiming yourself — with
              varied reflections, check-ins and grounding along the way.
            </span>
            <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#C9A227] px-5 py-2.5 text-sm font-semibold text-[#15120f]">
              Enter the journey <ArrowRight className="h-4 w-4" />
            </span>
          </Link>


          <TopicFilter heading="Browse by Topic" topics={TOPICS} value={topic} onChange={setTopic} />

          {/* Journey topics */}
          <Section
            title="Your Path"
            subtitle="Choose a path that calls to you today — part of My Recovery Journey."
          >
            {visibleJourney.length === 0 ? (
              <EmptyTopic label="paths" onReset={() => setTopic(null)} />
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {visibleJourney.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setActiveTopic(t)}
                    className={`group relative overflow-hidden rounded-3xl border border-white/60 bg-gradient-to-br ${ACCENT[t.accent]} p-6 text-start shadow-sm backdrop-blur transition-all hover:-translate-y-1 hover:shadow-lg dark:border-white/10 dark:bg-none dark:bg-card/70`}
                  >
                    <div className="pointer-events-none absolute inset-0 bg-white/30 dark:bg-transparent" />
                    <div className="relative">
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-3xl">{t.emoji}</span>
                        {completed.has(`topic:${t.id}`) && (
                          <CheckCircle2 className="h-5 w-5 text-[#7a5cb0]/80 dark:text-[#c9b6ec]" />
                        )}
                      </div>
                      <h3 className="font-serif text-xl text-foreground">
                        {t.title}
                      </h3>
                      <p className="mt-2 text-sm italic leading-relaxed text-foreground/80">
                        "{t.tagline}"
                      </p>
                      <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-foreground/90 group-hover:translate-x-0.5 transition-transform">
                        Explore <ArrowRight className="h-3.5 w-3.5" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Section>


          {/* Reparenting picker */}
          <Section
            title="Reparenting Practice"
            subtitle="What do you need most today? Choose, and receive guidance."
          >
            {visibleNeeds.length === 0 ? (
              <EmptyTopic label="reparenting needs" onReset={() => setTopic(null)} />
            ) : (
              <div className="flex flex-wrap gap-3">
                {visibleNeeds.map((n) => {
                  const selected = activeNeed?.id === n.id;
                  return (
                    <button
                      key={n.id}
                      onClick={() =>
                        setActiveNeed((prev) => (prev?.id === n.id ? null : n))
                      }
                      className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-medium transition-all ${
                        selected
                          ? "border-[#9b7cc7] bg-[#9b7cc7] text-white shadow-md"
                          : "border-white/70 bg-white/70 text-foreground/85 hover:border-[#d9c8f5] hover:bg-white dark:border-white/10 dark:bg-card/70 dark:hover:border-[#9b7cc7]/50 dark:hover:bg-card"
                      }`}
                    >
                      <span className="text-base">{n.emoji}</span>
                      {n.label}
                    </button>
                  );
                })}
              </div>
            )}


            {activeNeed && (
              <div className="mt-6 overflow-hidden rounded-3xl border border-[#d9c8f5]/60 bg-gradient-to-br from-white via-[#faf5fc] to-[#f3e7f7] p-6 shadow-sm sm:p-8 dark:border-[#9b7cc7]/30 dark:bg-none dark:bg-card/70">
                <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-[#7a5cb0] dark:text-[#c9b6ec]">
                  <Heart className="h-3.5 w-3.5" />
                  For when you need {activeNeed.label.toLowerCase()}
                </div>
                <p className="text-base leading-relaxed text-foreground/90">
                  {activeNeed.guidance}
                </p>
                <div className="mt-5 rounded-2xl border border-[#d9c8f5]/50 bg-white/70 p-4 dark:border-white/10 dark:bg-white/5">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-[#c48bb8] dark:text-[#e2a6d0]">
                    Affirmation
                  </div>
                  <p className="mt-1 font-serif text-lg italic text-foreground">
                    "{activeNeed.affirmation}"
                  </p>
                </div>
              </div>
            )}
          </Section>

          {/* Wounds */}
          <Section
            title="Understanding My Wounds"
            subtitle="Naming what shaped you is the beginning of softening it."
          >
            {visibleWounds.length === 0 ? (
              <EmptyTopic label="wounds" onReset={() => setTopic(null)} />
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {visibleWounds.map((w) => (
                  <button
                    key={w.id}
                    onClick={() => setActiveWound(w)}
                    className="group relative overflow-hidden rounded-3xl border border-white/70 bg-white/70 p-6 text-start backdrop-blur transition-all hover:-translate-y-0.5 hover:border-[#d9c8f5] hover:shadow-md dark:border-white/10 dark:bg-card/70 dark:hover:border-[#9b7cc7]/50"
                  >
                    <div className="mb-2 flex items-center gap-3">
                      <span className="text-2xl">{w.emoji}</span>
                      <h3 className="font-serif text-lg text-foreground">
                        {w.name}
                      </h3>
                    </div>
                    <p className="text-sm leading-relaxed text-foreground/75">
                      {w.description}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-[#7a5cb0] group-hover:translate-x-0.5 transition-transform dark:text-[#c9b6ec]">
                      Explore this wound{" "}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Section>


          <RecentReflections />

          <div className="mt-12 text-center text-sm text-foreground/70">
            Reflections you save here will appear in your{" "}
            <Link
              to="/journal"
              className="font-medium text-[#7a5cb0] underline underline-offset-2 dark:text-[#c9b6ec]"
            >
              Recovery Journal
            </Link>
            .
          </div>
        </div>

        {/* Modals */}
        {activeTopic && (
          <TopicFlow
            topic={activeTopic}
            onClose={() => setActiveTopic(null)}
            onComplete={() => {
              markComplete(`topic:${activeTopic.id}`);
            }}
          />
        )}
        {activeWound && (
          <WoundDialog
            wound={activeWound}
            onClose={() => setActiveWound(null)}
          />
        )}
      </div>
    </BeforeYouBegin>
  );
}

// ───────── Layout helpers ─────────

function EmptyTopic({ label, onReset }: { label: string; onReset: () => void }) {
  return (
    <div className="rounded-3xl border border-white/70 bg-white/60 p-8 text-center text-sm text-[#5a5775] backdrop-blur-md dark:border-white/10 dark:bg-card/60 dark:text-foreground/70">
      <Compass className="mx-auto mb-2 h-6 w-6 text-[#7a6ca8] dark:text-[#c9b6ec]" />
      <p className="font-medium text-foreground">No {label} match this topic yet.</p>
      <button
        type="button"
        onClick={onReset}
        className="mt-3 text-xs font-medium text-[#7a5cb0] underline underline-offset-2 dark:text-[#c9b6ec]"
      >
        Clear topic filter
      </button>
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-14">
      <h2 className="font-serif text-2xl text-foreground sm:text-3xl">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-1 mb-6 text-sm text-foreground/70">{subtitle}</p>
      )}
      {children}
    </section>
  );
}

function AmbientBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 -z-0 dark:opacity-40">
      <div className="absolute -top-32 start-1/2 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-[#f1d6ec]/50 blur-3xl" />
      <div className="absolute top-40 end-[-4rem] h-72 w-72 rounded-full bg-[#e3d4f7]/50 blur-3xl" />
      <div className="absolute top-[32rem] start-[-3rem] h-72 w-72 rounded-full bg-[#d8e6d2]/40 blur-3xl" />
      <div className="absolute bottom-0 end-1/4 h-80 w-80 rounded-full bg-[#fbe1e0]/50 blur-3xl" />

      <Star
        className="absolute top-16 start-[8%] h-3 w-3 animate-pulse text-amber-300/70"
        style={{ animationDuration: "4s" }}
      />
      <Star
        className="absolute top-40 end-[16%] h-2 w-2 animate-pulse text-amber-200/70"
        style={{ animationDuration: "5s" }}
      />
      <Star
        className="absolute top-[20rem] start-[24%] h-2.5 w-2.5 animate-pulse text-amber-300/60"
        style={{ animationDuration: "6s" }}
      />
      <Star
        className="absolute top-[40rem] end-[10%] h-2 w-2 animate-pulse text-amber-200/60"
        style={{ animationDuration: "5.5s" }}
      />

      <Moon className="absolute top-24 end-[8%] h-5 w-5 text-[#c9b6ec]/60" />
      <Moon className="absolute bottom-32 start-[18%] h-4 w-4 text-[#c9b6ec]/50" />

      <Cloud className="absolute top-[12rem] start-[5%] h-10 w-10 text-white/60 dark:text-white/15" />
      <Cloud className="absolute top-[44rem] end-[14%] h-12 w-12 text-white/50 dark:text-white/10" />

      <Leaf className="absolute top-[26rem] end-[6%] h-5 w-5 -rotate-12 text-[#a8c4a0]/60" />
      <Leaf className="absolute bottom-44 start-[7%] h-4 w-4 rotate-45 text-[#a8c4a0]/60" />
    </div>
  );
}

// ───────── Topic interactive flow ─────────

type Step = "intro" | "prompt" | "result";

/**
 * Shared modal behaviour: close on Escape and stop the page behind the sheet
 * from scrolling while it is open (iOS Safari otherwise scrolls the page).
 */
function useModalShell(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
}

const draftKey = (id: string) => `rt:shadow:draft:${id}`;



function TopicFlow({
  topic,
  onClose,
  onComplete,
}: {
  topic: JourneyTopic;
  onClose: () => void;
  onComplete: () => void;
}) {
  const [step, setStep] = useState<Step>("intro");
  const [content, setContent] = useState("");
  const [ai, setAi] = useState<AiResponse | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useModalShell(onClose);

  // Nothing typed here may be lost: keep a local draft until the save is
  // confirmed, and restore it if the sheet is reopened.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(draftKey(topic.id));
      if (saved) {
        setContent(saved);
        setStep("prompt");
      }
    } catch {
      /* noop */
    }
  }, [topic.id]);

  useEffect(() => {
    if (!content.trim()) return;
    const id = window.setTimeout(() => {
      try {
        localStorage.setItem(draftKey(topic.id), content);
      } catch {
        /* noop */
      }
    }, 400);
    return () => window.clearTimeout(id);
  }, [content, topic.id]);

  // Each step starts at the top of the sheet rather than mid-scroll.
  useEffect(() => {
    panelRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const saveEntry = useServerFn(createJournalEntry);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (vars: { content: string }) =>
      saveEntry({
        data: {
          title: `Shadow Work: ${topic.title}`,
          content: vars.content,
          prompt: topic.prompt,
        },
      }),
    onSuccess: (res) => {
      const entry = res.entry as unknown as { ai_response: AiResponse | null };
      setAi(entry.ai_response);
      setStep("result");
      onComplete();
      try {
        localStorage.removeItem(draftKey(topic.id));
      } catch {
        /* noop */
      }
      queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
      toast.success("Your reflection is saved.");
    },
    onError: (e: Error) =>
      toast.error(friendlyError(e, "Could not save your reflection."), {
        description: "Your writing is still here — you can try saving again.",
        duration: 8000,
      }),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[#2a1e3f]/40 p-0 backdrop-blur-sm sm:items-center sm:p-6 dark:bg-black/60"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={topic.title}
        className="relative max-h-[92dvh] w-full max-w-2xl overflow-y-auto overscroll-contain rounded-t-3xl border border-[#d9c8f5]/70 bg-gradient-to-br from-white via-[#faf5fc] to-[#f7ecd5]/60 pb-[env(safe-area-inset-bottom)] shadow-2xl sm:rounded-3xl dark:border-[#9b7cc7]/30 dark:bg-none dark:bg-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#d9c8f5]/40 bg-white/85 px-6 py-4 backdrop-blur dark:border-white/10 dark:bg-card/90">
          <div className="flex items-center gap-2">
            <span className="text-xl">{topic.emoji}</span>
            <span className="font-serif text-base text-foreground">
              {topic.title}
            </span>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-7 sm:px-8 sm:py-9">
          {/* Step indicator */}
          <div className="mb-6 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-[#7a5cb0] dark:text-[#c9b6ec]">
            <Sparkles className="h-3.5 w-3.5" />
            {step === "intro"
              ? "Step 1 of 3 · Setting the scene"
              : step === "prompt"
                ? "Step 2 of 3 · Your reflection"
                : "Step 3 of 3 · A gentle response"}
          </div>

          {step === "intro" && (
            <div>
              <p className="font-serif text-2xl italic leading-snug text-foreground">
                "{topic.tagline}"
              </p>
              <p className="mt-5 text-base leading-relaxed text-foreground/85">
                {topic.intro}
              </p>
              <div className="mt-8 flex justify-end">
                <button
                  onClick={() => setStep("prompt")}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#9b7cc7] to-[#c48bb8] px-6 py-3 text-sm font-medium text-white shadow-md transition-transform hover:scale-[1.02]"
                >
                  Continue gently
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {step === "prompt" && (
            <div>
              <p className="font-serif text-2xl leading-snug text-foreground">
                {topic.prompt}
              </p>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={7}
                autoFocus
                placeholder="There is no wrong way to begin. Let it come as it comes…"
                className="mt-5 w-full resize-none rounded-2xl border border-[#d9c8f5]/60 bg-white/80 p-4 font-serif text-base leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:border-[#9b7cc7] focus:outline-none focus:ring-2 focus:ring-[#9b7cc7]/20 dark:border-white/15 dark:bg-white/5"
              />
              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={() => setStep("intro")}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button
                  onClick={() =>
                    content.trim() && mutation.mutate({ content: content.trim() })
                  }
                  disabled={!content.trim() || mutation.isPending}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#9b7cc7] to-[#c48bb8] px-6 py-3 text-sm font-medium text-white shadow-md transition-transform hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                >
                  {mutation.isPending ? (
                    <>
                      <Sparkles className="h-4 w-4 animate-pulse" />
                      Receiving reflection…
                    </>
                  ) : (
                    <>
                      Receive reflection <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {step === "result" && (
            <div className="space-y-5">
              {ai ? (
                <>
                  <ResultBlock
                    icon={<Sparkles className="h-3.5 w-3.5" />}
                    label="A gentle reflection"
                    tint="lavender"
                  >
                    <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">
                      {ai.reflection}
                    </p>
                  </ResultBlock>

                  {ai.insight && (
                    <ResultBlock
                      icon={<Lightbulb className="h-3.5 w-3.5" />}
                      label="Healing insight"
                      tint="cream"
                    >
                      <p className="text-sm leading-relaxed text-foreground/90">
                        {ai.insight}
                      </p>
                    </ResultBlock>
                  )}

                  {ai.practice && (
                    <ResultBlock
                      icon={<Heart className="h-3.5 w-3.5" />}
                      label="Suggested practice"
                      tint="blush"
                    >
                      <p className="font-serif text-lg text-foreground">
                        {ai.practice.title}
                      </p>
                      <p className="mt-1 text-sm text-foreground/85">
                        {ai.practice.description}
                      </p>
                      <Link
                        to={ai.practice.href}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-xs font-medium text-background transition-transform hover:scale-[1.02]"
                      >
                        Open practice <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </ResultBlock>
                  )}

                  {ai.followUp && (
                    <ResultBlock
                      icon={<Compass className="h-3.5 w-3.5" />}
                      label="A question to carry with you"
                      tint="sage"
                    >
                      <p className="font-serif text-base italic text-foreground">
                        "{ai.followUp}"
                      </p>
                    </ResultBlock>
                  )}
                </>
              ) : (
                <ResultBlock
                  icon={<Sparkles className="h-3.5 w-3.5" />}
                  label="A gentle reflection"
                  tint="lavender"
                >
                  <p className="text-sm text-foreground/85">
                    Thank you for taking this moment. Your reflection is saved
                    in your Recovery Journal.
                  </p>
                </ResultBlock>
              )}

              {/* Built-in practice from the topic itself */}
              <ResultBlock
                icon={<Wind className="h-3.5 w-3.5" />}
                label={`${topic.title} · practice`}
                tint="cream"
              >
                <p className="font-serif text-lg text-foreground">
                  {topic.practice.title}
                </p>
                <p className="mt-1 text-sm text-foreground/85">
                  {topic.practice.body}
                </p>
              </ResultBlock>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={onClose}
                  className="rounded-full px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground"
                >
                  Close gently
                </button>
                <Link
                  to="/journal"
                  className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#9b7cc7] to-[#c48bb8] px-5 py-2.5 text-sm font-medium text-white shadow-md"
                >
                  View in journal <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultBlock({
  icon,
  label,
  tint,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  tint: "lavender" | "cream" | "blush" | "sage";
  children: React.ReactNode;
}) {
  const tints: Record<
    "lavender" | "cream" | "blush" | "sage",
    { box: string; chip: string }
  > = {
    lavender: {
      box: "border-[#d9c8f5]/60 bg-gradient-to-br from-[#f3e7f7] to-white dark:border-[#9b7cc7]/30 dark:bg-none dark:bg-white/5",
      chip: "text-[#7a5cb0] dark:text-[#c9b6ec]",
    },
    cream: {
      box: "border-[#efdcb6]/70 bg-gradient-to-br from-[#f7ecd5]/70 to-white dark:border-[#efdcb6]/25 dark:bg-none dark:bg-white/5",
      chip: "text-[#a87f3f] dark:text-[#e0c48a]",
    },
    blush: {
      box: "border-[#f5cbc9]/70 bg-gradient-to-br from-[#fbe1e0]/80 to-white dark:border-[#f5cbc9]/25 dark:bg-none dark:bg-white/5",
      chip: "text-[#b56a6a] dark:text-[#e8a5a5]",
    },
    sage: {
      box: "border-[#bfd4b6]/60 bg-gradient-to-br from-[#d8e6d2]/60 to-white dark:border-[#bfd4b6]/25 dark:bg-none dark:bg-white/5",
      chip: "text-[#5d8a5d] dark:text-[#a9cf9f]",
    },
  };
  const t = tints[tint];
  return (
    <div className={`rounded-2xl border ${t.box} p-5`}>
      <div
        className={`mb-2 inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider ${t.chip}`}
      >
        {icon}
        {label}
      </div>
      {children}
    </div>
  );
}


// ───────── Wound dialog ─────────

function WoundDialog({ wound, onClose }: { wound: Wound; onClose: () => void }) {
  useModalShell(onClose);
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[#2a1e3f]/40 p-0 backdrop-blur-sm sm:items-center sm:p-6 dark:bg-black/60"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={wound.name}
        className="relative max-h-[92dvh] w-full max-w-xl overflow-y-auto overscroll-contain rounded-t-3xl border border-[#d9c8f5]/70 bg-gradient-to-br from-white via-[#faf5fc] to-[#fbe1e0]/40 pb-[env(safe-area-inset-bottom)] shadow-2xl sm:rounded-3xl dark:border-[#9b7cc7]/30 dark:bg-none dark:bg-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#d9c8f5]/40 bg-white/85 px-6 py-4 backdrop-blur dark:border-white/10 dark:bg-card/90">
          <div className="flex items-center gap-2">
            <span className="text-xl">{wound.emoji}</span>
            <span className="font-serif text-base text-foreground">
              {wound.name}
            </span>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-5 px-6 py-7 sm:px-8 sm:py-8">
          <p className="text-base leading-relaxed text-foreground/85">
            {wound.description}
          </p>

          <div className="rounded-2xl border border-[#d9c8f5]/50 bg-white/70 p-5 dark:border-white/10 dark:bg-white/5">
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-[#7a5cb0] dark:text-[#c9b6ec]">
              How it can show up in adulthood
            </div>
            <ul className="space-y-1.5 text-sm text-foreground/85">
              {wound.adultPatterns.map((p, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#c48bb8] dark:text-[#e2a6d0]">•</span>
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-[#efdcb6]/60 bg-gradient-to-br from-[#f7ecd5]/60 to-white p-5 dark:border-[#efdcb6]/25 dark:bg-none dark:bg-white/5">
            <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-[#a87f3f] dark:text-[#e0c48a]">
              Healing insight
            </div>
            <p className="text-sm leading-relaxed text-foreground/90">
              {wound.insight}
            </p>
          </div>

          <div className="rounded-2xl border border-[#d9c8f5]/50 bg-white/70 p-5 dark:border-white/10 dark:bg-white/5">
            <div className="mb-1 flex items-start justify-between gap-2">
              <div className="text-[11px] font-medium uppercase tracking-wider text-[#7a5cb0] dark:text-[#c9b6ec]">
                Reflection
              </div>
              <FavoriteButton
                storageKey="rt:fav-shadow-work"
                id={wound.id}
                label={wound.reflection}
                sub={wound.name}
                to="/shadow-work"
                size="sm"
              />
            </div>
            <p className="font-serif text-base italic text-foreground">
              "{wound.reflection}"
            </p>
          </div>

          <div className="rounded-2xl border border-[#f5cbc9]/60 bg-gradient-to-br from-[#fbe1e0]/60 to-white p-5 dark:border-[#f5cbc9]/25 dark:bg-none dark:bg-white/5">
            <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-[#b56a6a] dark:text-[#e8a5a5]">
              A small action step
            </div>
            <p className="text-sm leading-relaxed text-foreground/90">
              {wound.actionStep}
            </p>
          </div>

          <div className="flex justify-end pt-2">
            <Link
              to="/journal"
              className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#9b7cc7] to-[#c48bb8] px-5 py-2.5 text-sm font-medium text-white shadow-md"
            >
              Reflect in journal <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ───────── Recent shadow work reflections ─────────

interface JournalEntryRow {
  id: string;
  title: string | null;
  content: string | null;
  created_at: string;
}

function RecentReflections() {
  const fetchEntries = useServerFn(getJournalEntries);
  const { data, isLoading } = useQuery({
    queryKey: ["journal-entries", "shadow-work-recent"],
    queryFn: () => fetchEntries({}),
    staleTime: 30_000,
  });

  const entries = ((data?.entries ?? []) as JournalEntryRow[])
    .filter((e) => (e.title ?? "").startsWith("Shadow Work:"))
    .slice(0, 3);

  if (isLoading) return null;

  if (entries.length === 0) {
    return (
      <section
        data-testid="shadow-recent-empty"
        className="mt-12 rounded-3xl border border-[#d9c8f5]/50 bg-white/60 p-6 text-center backdrop-blur dark:border-[#9b7cc7]/30 dark:bg-card/60"
      >
        <h2 className="font-serif text-xl text-foreground">
          Recent reflections
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground/70">
          Nothing saved yet. When you write your first shadow work reflection, it will appear here so you can revisit what surfaced.
        </p>
        <p className="mx-auto mt-3 max-w-md text-xs text-foreground/60">
          Pick a prompt above, take your time, and press <span className="font-medium text-[#7a5cb0] dark:text-[#c9b6ec]">Save reflection</span> when you're ready.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-12 rounded-3xl border border-[#d9c8f5]/50 bg-white/60 p-6 backdrop-blur dark:border-[#9b7cc7]/30 dark:bg-card/60">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h2 className="font-serif text-xl text-foreground">
          Recent reflections
        </h2>
        <Link
          to="/journal"
          className="text-xs font-medium text-[#7a5cb0] underline underline-offset-2 dark:text-[#c9b6ec]"
        >
          View all →
        </Link>
      </div>
      <ul className="divide-y divide-[#e6dbf5]/70 dark:divide-white/10">
        {entries.map((e) => {
          const topic = (e.title ?? "").replace(/^Shadow Work:\s*/, "") || "Reflection";
          const body = (e.content ?? "").trim();
          const preview =
            body.length > 180 ? `${body.slice(0, 180).trimEnd()}…` : body;
          return (
            <li key={e.id} className="py-3 first:pt-0 last:pb-0">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-medium uppercase tracking-wide text-[#7a5cb0] dark:text-[#c9b6ec]">
                  {topic}
                </span>
                <span className="text-[11px] text-foreground/60">
                  {new Date(e.created_at).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                  })}
                </span>
              </div>
              {preview ? (
                <p
                  data-testid="shadow-recent-body"
                  className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground/85"
                >
                  {preview}
                </p>
              ) : (
                <p className="mt-1.5 text-sm italic text-foreground/60">
                  (no text saved)
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
